import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import CommonModal from "./CommonModal";
import { callAPI } from "../../utils/apiUtils";
import { apiUrls } from "../../utils/apiUrls";

const emptySettings = {
  auto_print_enabled: false,
  auto_print_time: "",
  auto_email_enabled: false,
  auto_email_time: "",
  auto_drive_enabled: false,
  auto_drive_time: "",
  drive_folder_id: "",
};

/**
 * Per-restaurant daily automation for the Revenue report: auto-print to the primary printer,
 * auto-email to the restaurant's registered email, and/or auto-upload to a shared Google Drive
 * folder — each at its own time of day. Each report covers the previous day's numbers (the day
 * that just closed), fired once daily by a backend cron job.
 */
const RevenueAutomationSettingsModal = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState(emptySettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await callAPI(apiUrls.REVENUE_AUTOMATION_SETTINGS);
      if (res.data?.data) {
        setSettings({
          auto_print_enabled: !!res.data.data.auto_print_enabled,
          auto_print_time: (res.data.data.auto_print_time || "").slice(0, 5),
          auto_email_enabled: !!res.data.data.auto_email_enabled,
          auto_email_time: (res.data.data.auto_email_time || "").slice(0, 5),
          auto_drive_enabled: !!res.data.data.auto_drive_enabled,
          auto_drive_time: (res.data.data.auto_drive_time || "").slice(0, 5),
          drive_folder_id: res.data.data.drive_folder_id || "",
        });
      }
    } catch (err) {
      console.error("Failed to fetch automation settings:", err);
      toast.error("Failed to load automation settings");
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await callAPI(apiUrls.REVENUE_UPDATE_AUTOMATION_SETTINGS, {}, "POST", settings);
      if (res.data?.status === 1) {
        toast.success("Automation settings saved");
        onClose();
      } else {
        toast.error(res.data?.message || "Failed to save settings");
      }
    } catch (err) {
      console.error("Failed to save automation settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="revenueAutomationSettingsModal">
      <div className="modal-header">
        <h5 className="modal-title">Revenue Report Automation</h5>
        <button className="btn-close" onClick={onClose}></button>
      </div>
      <div className="modal-body">
        {loading ? (
          <p className="text-center">Loading...</p>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
              Each automation fires once daily for the previous day's report.
            </p>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  className="me-2"
                  checked={settings.auto_print_enabled}
                  onChange={(e) => update("auto_print_enabled", e.target.checked)}
                />
                Auto-print to primary printer
              </label>
              <input
                type="time"
                className="form-control form-control-sm"
                style={{ width: 120 }}
                value={settings.auto_print_time}
                disabled={!settings.auto_print_enabled}
                onChange={(e) => update("auto_print_time", e.target.value)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  className="me-2"
                  checked={settings.auto_email_enabled}
                  onChange={(e) => update("auto_email_enabled", e.target.checked)}
                />
                Auto-email to restaurant email
              </label>
              <input
                type="time"
                className="form-control form-control-sm"
                style={{ width: 120 }}
                value={settings.auto_email_time}
                disabled={!settings.auto_email_enabled}
                onChange={(e) => update("auto_email_time", e.target.value)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  className="me-2"
                  checked={settings.auto_drive_enabled}
                  onChange={(e) => update("auto_drive_enabled", e.target.checked)}
                />
                Auto-upload to Google Drive
              </label>
              <input
                type="time"
                className="form-control form-control-sm"
                style={{ width: 120 }}
                value={settings.auto_drive_time}
                disabled={!settings.auto_drive_enabled}
                onChange={(e) => update("auto_drive_time", e.target.value)}
              />
            </div>

            {settings.auto_drive_enabled && (
              <div className="mb-2">
                <label className="form-label" style={{ fontSize: 13 }}>
                  Google Drive folder ID
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="e.g. 1AbCdEfGhIjKlMnOpQrStUvWxYz"
                  value={settings.drive_folder_id}
                  onChange={(e) => update("drive_folder_id", e.target.value)}
                />
                <small style={{ color: "#888" }}>
                  Share this Drive folder (Editor access) with the OrderArt reporting service
                  account, then paste the folder's id from its URL here.
                </small>
              </div>
            )}
          </>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={loading || saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </CommonModal>
  );
};

export default RevenueAutomationSettingsModal;
